import { motion } from "framer-motion";

export const PageHeader = ({ overline, title, subtitle, image, align = "left" }) => {
    if (image) {
        // Visible image layout: image takes the right side prominently, text sits on a navy panel on the left
        return (
            <section className="relative bg-navy-900 text-editorial overflow-hidden" data-testid="page-header">
                <div className="grid lg:grid-cols-12 min-h-[420px] md:min-h-[480px]">
                    {/* LEFT — Text panel */}
                    <div className="lg:col-span-6 xl:col-span-5 relative flex items-center px-6 lg:px-12 xl:px-16 py-16 md:py-20 lg:py-24 z-10">
                        <div className="absolute inset-0 bg-navy-900" />
                        <div className="absolute inset-0 bg-noise opacity-30 pointer-events-none" />
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.9, ease: [0.21, 0.47, 0.32, 0.98] }}
                            className="relative max-w-xl"
                        >
                            {overline && <p className="overline mb-5">{overline}</p>}
                            <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl leading-[1.04] tracking-tight text-balance">
                                {title}
                            </h1>
                            {subtitle && (
                                <p className="mt-5 md:mt-7 text-base md:text-lg text-editorial/80 leading-relaxed">
                                    {subtitle}
                                </p>
                            )}
                            <div className="mt-8 heartbeat-line w-32" />
                        </motion.div>
                    </div>

                    {/* RIGHT — Visible image */}
                    <motion.div
                        initial={{ opacity: 0, scale: 1.04 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 1.4, ease: [0.21, 0.47, 0.32, 0.98] }}
                        className="lg:col-span-6 xl:col-span-7 relative overflow-hidden min-h-[300px] lg:min-h-[480px]"
                    >
                        <img
                            src={image}
                            alt=""
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="eager"
                        />
                        {/* Soft left-edge bleed into navy panel for seamless feel */}
                        <div className="absolute inset-y-0 left-0 w-24 lg:w-32 bg-gradient-to-r from-navy-900 to-transparent pointer-events-none" />
                        {/* Subtle bottom gradient for legibility/depth */}
                        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-navy-900/60 to-transparent pointer-events-none" />
                    </motion.div>
                </div>
            </section>
        );
    }

    // No image — original solid navy header
    return (
        <section className="relative bg-navy-900 text-editorial overflow-hidden" data-testid="page-header">
            <div className="absolute inset-0 bg-noise opacity-40 pointer-events-none" />
            <div className={`relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-20 md:pt-32 md:pb-28 ${align === "center" ? "text-center" : ""}`}>
                {overline && <p className="overline mb-5">{overline}</p>}
                <h1 className={`font-serif font-light text-5xl md:text-6xl lg:text-7xl leading-[1.04] tracking-tight ${align === "center" ? "mx-auto max-w-4xl" : "max-w-5xl"} text-balance`}>
                    {title}
                </h1>
                {subtitle && (
                    <p className={`mt-6 md:mt-8 text-lg md:text-xl text-editorial/75 leading-relaxed ${align === "center" ? "mx-auto max-w-2xl" : "max-w-2xl"}`}>
                        {subtitle}
                    </p>
                )}
                <div className="mt-10 heartbeat-line w-32" />
            </div>
        </section>
    );
};

export default PageHeader;
